import type { DrawHistorySummary, WonPrize } from "@/api/model"
import { computed, type Ref } from "vue"

import { getDrawName } from "@/views/myaccount/loyalty/getDrawName"

const WEEK_MS = 7 * 24 * 60 * 60 * 1000
/** Show the modal only while the latest win is this recent. */
const SHOW_WINDOW_MS = 4 * WEEK_MS

/** The draw with the most recent non-null `wonAt`, or null if the account has not won. */
function findLatestWonDraw(
  draws: DrawHistorySummary[],
): DrawHistorySummary | null {
  let latest: DrawHistorySummary | null = null
  for (const draw of draws) {
    if (!draw.wonAt) continue
    if (!latest || new Date(draw.wonAt) > new Date(latest.wonAt as string)) {
      latest = draw
    }
  }
  return latest
}

/**
 * Drives the So Rewarding "you won" modal.
 *
 * Shows the modal when the most recent win across the account's draws happened
 * within the last 4 weeks, unless `isSuppressed` (see useYouWonSuppression,
 * which also gates the loyalty-status fetch so we don't call when suppressed).
 */
export function useYouWonModal(
  draws: Ref<DrawHistorySummary[] | null | undefined>,
  isSuppressed: Ref<boolean>,
) {
  const latestWonDraw = computed(() => findLatestWonDraw(draws.value ?? []))

  const wonPrize = computed<WonPrize | null>(
    () => latestWonDraw.value?.wonPrize ?? null,
  )

  const wonDrawName = computed<string | null>(() => {
    const draw = latestWonDraw.value
    return draw ? getDrawName(draw) : null
  })

  const showModal = computed(() => {
    if (isSuppressed.value) return false
    const wonAt = latestWonDraw.value?.wonAt
    if (!wonAt) return false
    return Date.now() - new Date(wonAt).getTime() < SHOW_WINDOW_MS
  })

  return { showModal, wonPrize, wonDrawName }
}
